import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { IMaskInput } from 'react-imask';
import editIcon from "../../../assets/images/editIcon02.png";
import saveIcon from "../../../assets/images/Check.svg";
import clearIcon from "../../../assets/images/X.svg";
import { formatToMMDDYYYY } from 'globalFunctions/formatToMMDDYYYY';
import { formatToYYYYMMDD } from 'globalFunctions/formatToYYYYMMDD';

const DobField = ({
  defaultValue,
  id,
  placeholder,
  onChange,
  showEditIcon,
  saveEdit,
  required,
  onFieldBlur,
  onEditClick,
  onCancelEdit,
  label
}) => {
  const initialValue = defaultValue ? formatToMMDDYYYY(defaultValue) : '';
  const [updatedVal, setUpdatedVal] = useState(initialValue);
  const [isEditable, setIsEditable] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setUpdatedVal(defaultValue ? formatToMMDDYYYY(defaultValue) : '');
  }, [defaultValue]);

  const validateDate = (value) => {
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
    return dateRegex.test(value) ? value : null;
  };

  const handleAccept = (value) => {
    const validDate = validateDate(value);
    if (validDate && (!showEditIcon || isEditable)) {
      setUpdatedVal(validDate);
      onChange(id, validDate);
    } else {
      setUpdatedVal(value);
    }
  };

  const handleSave = () => {
    const dobVal = formatToYYYYMMDD(updatedVal);
    const isValueEdited = defaultValue !== dobVal;
    saveEdit(id, dobVal, isValueEdited);
    setIsEditable(false);
  };

  const handleClear = () => {
    setUpdatedVal(initialValue);
    onChange(id, initialValue);
    setIsEditable(false);
    if (onCancelEdit) onCancelEdit();
  };

  const handleBlur = (e) => {
    if (
      onFieldBlur &&
      isEditable &&
      wrapperRef.current &&
      !wrapperRef.current.contains(e.relatedTarget)
    ) {
      onFieldBlur();
    }
  };

  return (
    <div
      ref={wrapperRef}
      role="group"
      className={`flex ${showEditIcon ? 'justify-between' : 'justify-start'}`}
      tabIndex="-1"
      onBlur={handleBlur}
    >
      <IMaskInput
        key={`${id}-${isEditable}-${initialValue}`}
        ref={inputRef}
        mask="MM/DD/YYYY"
        lazy={false}
        blocks={{
          MM: { mask: '00', placeholderChar: 'M' },
          DD: { mask: '00', placeholderChar: 'D' },
          YYYY: { mask: '0000', placeholderChar: 'Y' },
        }}
        defaultValue={initialValue}
        className="uppercase border-none outline-none h-4 mt-2 text-sm placeholder-grayNobel text-tileGray font-normal"
        readOnly={showEditIcon ? !isEditable : false}
        onAccept={handleAccept}
        required={required}
        type="tel"
        placeholder={placeholder}
      />

      {showEditIcon && !isEditable && (
        <button
          type="button"
          onClick={() => {
            setIsEditable(true);
            wrapperRef.current?.focus();
            if (onEditClick) onEditClick(label, true);
          }}
          className="ml-ten -mt-1 h-4 w-4 cursor-pointer bg-transparent border-none p-0"
        >
          <img src={editIcon} alt="edit" className="h-4 w-4" />
        </button>
      )}

      {isEditable && (
        <div className="save-cancel-wrapper grid -mt-5 ml-2 gap-2">
          <button
            type="button"
            onClick={handleSave}
            className="bg-transparent border-none p-0 cursor-pointer"
          >
            <img src={saveIcon} alt="Save" className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="bg-transparent border-none p-0 cursor-pointer"
          >
            <img src={clearIcon} alt="Cancel" className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

DobField.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showEditIcon: PropTypes.bool,
  saveEdit: PropTypes.func,
  required: PropTypes.bool,
  onFieldBlur: PropTypes.func,
  onEditClick: PropTypes.func,
  onCancelEdit: PropTypes.func,
  label: PropTypes.string,
};

DobField.defaultProps = {
  defaultValue: '',
  placeholder: 'MM/DD/YYYY',
  showEditIcon: false,
  saveEdit: () => {},
  required: false,
};

export default DobField;
